import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { engineerService } from '../../services/engineerService';
import { PageHeader, Button, Badge } from '../../components/ui';

const statusTone = (
  status: string,
  adminApproval?: string | null,
): { tone: 'success' | 'warning' | 'neutral' | 'info' | 'danger'; label: string } => {
  if (adminApproval === 'PENDING') return { tone: 'warning', label: 'Awaiting admin' };
  if (adminApproval === 'REJECTED') return { tone: 'danger', label: 'Rejected' };
  switch ((status || '').toUpperCase()) {
    case 'ACCEPTED':
      return { tone: 'success', label: 'Accepted' };
    case 'SENT':
      return { tone: 'info', label: 'Sent' };
    case 'DRAFT':
      return { tone: 'neutral', label: 'Draft' };
    case 'DECLINED':
      return { tone: 'danger', label: 'Declined' };
    default:
      return { tone: 'neutral', label: status || 'Unknown' };
  }
};

const EngineerQuotesList: React.FC = () => {
  const navigate = useNavigate();
  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ['engineer', 'quotes'],
    queryFn: () => engineerService.listMyQuotes(),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="My quotes"
        subtitle="Quick quotes you have raised from site. Tap one to review, or start a new one."
        actions={
          <Button
            size="sm"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={() => navigate('/engineer/quotes/new')}
          >
            New quote
          </Button>
        }
      />

      {isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading your quotes…
        </div>
      ) : quotes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <DocumentTextIcon className="w-8 h-8 mx-auto text-slate-400" />
          <h3 className="mt-3 text-sm font-semibold text-slate-900">
            No quotes yet
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Raise a quote on site directly from your company's price book.
          </p>
          <div className="mt-4">
            <Button
              size="sm"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={() => navigate('/engineer/quotes/new')}
            >
              Start a quote
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map((q) => {
            const tone = statusTone(q.status, q.adminApprovalStatus);
            return (
              <Link
                key={q.id}
                to={`/engineer/quotes/${q.id}`}
                className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{q.quoteNumber}</span>
                      <span aria-hidden>·</span>
                      <span>
                        {new Date(q.createdAt).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900 truncate">
                      {q.jobTitle || 'Quote'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-900">
                      £{Number(q.totalAmount || 0).toFixed(2)}
                    </div>
                    <Badge tone={tone.tone}>
                      {tone.tone === 'warning' && (
                        <ClockIcon className="w-3 h-3 inline mr-1" />
                      )}
                      {tone.tone === 'success' && (
                        <CheckBadgeIcon className="w-3 h-3 inline mr-1" />
                      )}
                      {tone.tone === 'danger' && (
                        <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                      )}
                      {tone.label}
                    </Badge>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EngineerQuotesList;
